import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CssBaseline from '@material-ui/core/CssBaseline'
import List from '@material-ui/core/List'
import { makeStyles } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import AssignmentReturnIcon from '@material-ui/icons/AssignmentReturn'
import BusinessIcon from '@material-ui/icons/Business'
import BuildIcon from '@material-ui/icons/Build'
import DashboardIcon from '@material-ui/icons/Dashboard'
import ListIcon from '@material-ui/icons/List'
import PeopleIcon from '@material-ui/icons/People'
import Security from '@material-ui/icons/Security'
import SettingsIcon from '@material-ui/icons/Settings'
import ApartmentIcon from '@material-ui/icons/Apartment'
import LocationCityIcon from '@material-ui/icons/LocationCity'
import ScheduleIcon from '@material-ui/icons/Schedule'
import clsx from 'clsx'
import {
  AssignmentsIcon,
  CertificateIcon,
  FormsIcon,
 
  JourneyIcons,
  LibraryIcon,
  LocationTrackingIcon,
  PolicyIcon,
  RasterImageIcon,
  ReportsIcon,
  TrainingMatrixIcon,
} from 'components/SvgIcons'
import { uiSettings } from 'etc/settings'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { getIsAdminLike } from 'utils/permissionHelpers'
import { IRootReduxState } from 'types/reducers'
import MenuListItem from './MenuListItem'
import MenuListItemContainer from './MenuListItemContainer'
import SideNavigation from './SideNavigation'
import EventIcon from '@material-ui/icons/Event'
import SchoolIcon from '@material-ui/icons/School'
import TopNavigation from './TopNavigation'
import FormatShapes from '@material-ui/icons/FormatShapes'
import { CompanyModuleAccessDto } from 'data/dtos/CompanyModuleAccessDto'
import SecuredModule from '../../components/navigation/SecuredModule'
import { Module } from 'enums/Module'
import { ModuleNoAccessVisibilityType } from 'enums/ModuleNoAccessVisibilityType'
import MessageIcon from '@material-ui/icons/Message'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined'
import Tracker from '@material-ui/icons/TrackChangesOutlined'
import Description from '@material-ui/icons/Description'
import AssignmentTurnedIn from '@material-ui/icons/AssignmentTurnedIn'
import NoteAdd from '@material-ui/icons/NoteAdd'
import Flag from '@material-ui/icons/Flag'
import AccountTree from '@material-ui/icons/AccountTree'
import Tune from '@material-ui/icons/Tune'
import Forum from '@material-ui/icons/Forum'
import History from '@material-ui/icons/History'
import ListAlt from '@material-ui/icons/ListAlt'
import { useLocation } from 'react-router-dom'
import TopHeaderDynamic from 'components/new-design-components/TopHeaderDynamic'

const useStyles = makeStyles((theme) => ({
  root: (props: any) => ({
    display: 'flex',
    position: 'relative',
    minHeight: '100%',
    background: theme.palette.type === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
        //paddingTop: !props.profile ? 0 : props.bigScreen ? uiSettings.header.appBarHeight : uiSettings.header.appBarHeightMobile,
        paddingTop: 0
  }),
  hide: {
    visibility: 'hidden',
  },
  /** Column: global page header + scrollable main */
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
  },
  content: {
    flexGrow: 1,
    padding: 0,
    minWidth: 0,
    minHeight: 0,
  },
  paddingY0: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  w1em: {
    width: '1em !important',
  },
  w110em: {
    width: '1.10em !important',
  },
  ml3: {
    marginLeft: '3px',
  },
}))

interface Props {
  showTopNavigation?: boolean
}
const FULLSCREEN_MAP_ROUTES = ['/locationTracking']
const SHELL_FREE_ROUTE_PREFIXES = ['/dynamic']

const Navigation: React.FC<Props> = ({ children, showTopNavigation = true }) => {
  const location = useLocation();
  const isFullscreenPage = FULLSCREEN_MAP_ROUTES.some(r => location.pathname === r)
  const isShellFreePage = SHELL_FREE_ROUTE_PREFIXES.some(r => location.pathname.startsWith(r))
  const isModuleActive = (routes: string[]) => routes.includes(location.pathname);
  const bigScreen = useMediaQuery('(min-width:601px)')
  const profile = useSelector((state: IRootReduxState) => state.app.user.current)
  const oidcUser = useSelector((state: IRootReduxState) => state.oidc.user)
  const moduleAccess = useSelector((state: IRootReduxState) => state.app.user.access)
  const classes = useStyles({ bigScreen, profile })

  const getModule = (module: Module) => {
    const permissions = moduleAccess?.find((i) => i.Module === module) as CompanyModuleAccessDto
    return permissions
  }

  // Returns the first accessible module from a list, or the first greyed-out one.
  // Used to gate containers whose children each have their own SecuredModule.
  const getAnyModule = (...modules: Module[]): CompanyModuleAccessDto | undefined => {
    const list = modules.map(getModule)
    return list.find(m => m?.AccessAllowed)
      ?? list.find(m => m?.Visibility === ModuleNoAccessVisibilityType.GreyedOut)
  }

  const getInitialMenu = (path: string): string | null => {
    if (path.startsWith('/library')) return 'library'
    if (path.startsWith('/trainingmatirx')) return 'trainingMatrix'
    if (path.startsWith('/forms') || path.startsWith('/form/templates')) return 'forms'
    if (path.startsWith('/assignments') || path === '/settings/assignmentStatus') return 'assignments'
    if (path.startsWith('/ScheduledItems')) return 'scheduledItems'
    if (path.startsWith('/assets')) return 'assets'
    if (path.startsWith('/settings/company') || path.startsWith('/setting/')) return 'settings'
    if (path === '/UsersList' || path.startsWith('/organization')) return 'userManagement'
    if (path.startsWith('/communication')) return 'communication'
    if (path.startsWith('/locationTracking')) return 'mapManagement'
    return null
  }

  const [openMenu, setOpenMenu] = React.useState<string | null>(() => getInitialMenu(location.pathname))
  const toggleMenu = (key: string) => setOpenMenu(prev => prev === key ? null : key)
  
  return (
    <div className={classes.root}>
      <CssBaseline />
      {oidcUser && showTopNavigation && <TopNavigation />}
      {oidcUser && !isShellFreePage && (
        <SideNavigation>
          <List className={classes.paddingY0}>
            <SecuredModule permissions={getModule(Module.Dashboard)}>
              <MenuListItem title={<FormattedMessage id="menu.main.dashboard" defaultMessage="Dashboard" />} icon={<DashboardIcon />} route="/" />
            </SecuredModule>

            <SecuredModule permissions={getModule(Module.Reports)}>
              <MenuListItem title={<FormattedMessage id="menu.main.reports" defaultMessage="Reports" />} icon={<ReportsIcon />} route="/reports" />
            </SecuredModule>

            <SecuredModule permissions={getModule(Module.Calendar)}>
              <MenuListItem title={<FormattedMessage id="menu.main.calendar" defaultMessage="Calendar" />} icon={<EventIcon />} route="/calendar" />
            </SecuredModule>

            <SecuredModule permissions={getModule(Module.JourneyManagement)}>
              <MenuListItem title={<FormattedMessage id="menu.main.journey" defaultMessage="Journey Management" />} icon={<JourneyIcons />} route="/journeyManagement" />
            </SecuredModule>

            {/* Schedule is hidden outright when its permission toggle is off, regardless of the "When not allowed" option. */}
            {getModule(Module.ScheduledItems)?.AccessAllowed && <SecuredModule permissions={getModule(Module.ScheduledItems)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.Schedule" defaultMessage="Schedule" />} icon={<ScheduleIcon />} isOpen={openMenu === 'schedule'} onToggle={() => toggleMenu('schedule')}>
                <List className={classes.paddingY0}>
                  <MenuListItem title={<FormattedMessage id="menu.main.scheduledItems.list" defaultMessage="Events" />} icon={<ScheduleIcon />} route="/ScheduledItems" />
                  {/* Event Type / Status / Visibility setup items are admin-only; normal users only see Events. */}
                  {getIsAdminLike(profile) && (
                    <>
                      <MenuListItem title={<FormattedMessage id="menu.main.scheduledItems.eventTypes" defaultMessage="Event Type" />} icon={<ListIcon />} route="/ScheduledItems/eventTypes" />
                      <MenuListItem title={<FormattedMessage id="menu.main.scheduledItems.statusTypes" defaultMessage="Status" />} icon={<ListIcon />} route="/ScheduledItems/statusTypes" />
                      <MenuListItem title={<FormattedMessage id="menu.main.scheduledItems.visibilityTypes" defaultMessage="Visibility" />} icon={<ListIcon />} route="/ScheduledItems/visibilityTypes" />
                    </>
                  )}
                </List>
              </MenuListItemContainer>
            </SecuredModule>}

            {/* <MenuListItem title={<FormattedMessage id="menu.main.courses" defaultMessage="Courses" />} icon={<CoursesIcon />} route="/courses" /> */}
            <SecuredModule permissions={getModule(Module.DocumentLibrary)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.library" defaultMessage="Library" />} icon={<LibraryIcon />} isOpen={openMenu === 'library'} onToggle={() => toggleMenu('library')}>
                <List className={classes.paddingY0}>
                  <MenuListItem title={<FormattedMessage id="menu.main.Documents" defaultMessage="Documents" />} icon={<Description />} route="/library/documents" />
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.culture-groups" defaultMessage="Culture Groups" />} icon={<FontAwesomeIcon icon="globe-americas" />} route="/library/cultureGroups" /> */}
                </List>
              </MenuListItemContainer>
            </SecuredModule>

            <SecuredModule permissions={getModule(Module.TrainingMatrix)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.trainingMatrix" defaultMessage="Training Matrix" />} icon={<TrainingMatrixIcon />} isOpen={openMenu === 'trainingMatrix'} onToggle={() => toggleMenu('trainingMatrix')}>
                <List className={classes.paddingY0}>
                  <MenuListItem title={<FormattedMessage id="menu.main.Course" defaultMessage="User Certifications" />} icon={<CertificateIcon />} route="/trainingmatirx/usercertificates" />
                  {/* Management pages are admin-only; normal users don't see them in the menu. */}
                  {(getIsAdminLike(profile) || profile?.SecurityLevel === 3) && (
                    <MenuListItem title={<FormattedMessage id="menu.main.courses" defaultMessage="Certification Management" />} icon={<LibraryIcon />} route="/trainingmatirx/courses" />
)}
                  <MenuListItem title={<FormattedMessage id="menu.main.privatecoures" defaultMessage="LMS Courses" />} icon={<SchoolIcon />} route="/trainingmatirx/Privatecourses" />
                  <MenuListItem title={<FormattedMessage id="menu.main.policies" defaultMessage="User Policy Compliance" />} icon={<PolicyIcon />} route="/trainingmatirx/policies" />
                  {(getIsAdminLike(profile) || profile?.SecurityLevel === 3) && (
                    <MenuListItem title={<FormattedMessage id="menu.main.companyPolicies" defaultMessage="User Policy Management" />} icon={<PolicyIcon />} route="/trainingmatirx/companypolicies" />
)}
                </List>
              </MenuListItemContainer>
            </SecuredModule>

            <SecuredModule permissions={getAnyModule(Module.CompletedForms, Module.BlankForms, Module.FormsPendingUpload, Module.FormsSearch)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.forms" defaultMessage="Forms" />} icon={<FormsIcon />} isOpen={openMenu === 'forms'} onToggle={() => toggleMenu('forms')}>
                <List className={classes.paddingY0}>
                  <SecuredModule permissions={getModule(Module.CompletedForms)}>
                    <MenuListItem title={<FormattedMessage id="menu.main.forms.completed" defaultMessage="Completed" />} icon={<AssignmentTurnedIn />} route="/forms/completed" />
                  </SecuredModule>
                  <SecuredModule permissions={getModule(Module.BlankForms)}>
                    <>
                      <MenuListItem title={<FormattedMessage id="menu.main.forms.templates" defaultMessage="Blank Forms" />} icon={<NoteAdd />} route="/form/templates" />
                      {/* Anonymous menu item hidden for all users (admins and normal users); route still exists. */}
                      {/* <MenuListItem
                        title={<FormattedMessage id="menu.main.forms.anonymous" defaultMessage="Anonymous" />}
                        icon={<FontAwesomeIcon icon="file-signature" className={clsx([classes.ml3, classes.w1em])} />}
                        route="/form/templates/anonymous"
                      /> */}
                    </>
                  </SecuredModule>
                </List>
              </MenuListItemContainer>
            </SecuredModule>
            <SecuredModule permissions={getModule(Module.Assignments)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.assignments.header" defaultMessage="Assignments" />} icon={<AssignmentsIcon />} isOpen={openMenu === 'assignments'} onToggle={() => toggleMenu('assignments')}>
                <List className={classes.paddingY0}>
                <MenuListItem title={<FormattedMessage id="menu.main.assignments.you" defaultMessage="Assigned To Me" />} icon={<AssignmentIndIcon />} route="/assignments/assignedToYou" />
                  {getIsAdminLike(profile) && (
                    <MenuListItem title={<FormattedMessage id="menu.main.assignments.other" defaultMessage="Assigned To Others" />} icon={<AssignmentReturnIcon />} route="/assignments/assignedByYou" />
                   )}
                  {getIsAdminLike(profile) && (
                    <MenuListItem title={<FormattedMessage id="menu.main.settings.AssignmentStatus" defaultMessage="Assignment Status" />} icon={<Flag />} route="/settings/assignmentStatus" />
                  )}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.assignment.recurring" defaultMessage="Recurring Assignment" />} icon={<RecurringAssignment/>} route="/assignment/recurring" /> */}
                </List>
              </MenuListItemContainer>
            </SecuredModule>




            <SecuredModule permissions={getModule(Module.CMMSAssets)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.assets.header" defaultMessage="Assets" />} icon={<ApartmentIcon />} isOpen={openMenu === 'assets'} onToggle={() => toggleMenu('assets')}>
                <List className={classes.paddingY0}>
                  {/* Asset Maintenance dashboard is admin-only; normal users don't see it in the menu. */}
                  {getIsAdminLike(profile) && (
                    <MenuListItem title={<FormattedMessage id="menu.main.maintenance" defaultMessage="Asset Maintenance" />} icon={<BuildIcon />} route="/assets/maintenance" />
                  )}
                  {/* Setup items are admin-only; normal users only see Asset Maintenance and Assets List. */}
                  {getIsAdminLike(profile) && (
                    <>
                      <MenuListItem title={<FormattedMessage id="menu.main.sites.locations.you" defaultMessage="Sites & Locations" />} icon={<LocationCityIcon />} route="/assets/sitesLocations" />
                      <MenuListItem title={<FormattedMessage id="menu.main.picklists" defaultMessage="Asset Configuration" />} icon={<ListIcon />} route="/assets/picklists" />
                      <MenuListItem title={<FormattedMessage id="menu.main.maintenance.schedules" defaultMessage="Maintenance Schedules" />} icon={<ScheduleIcon />} route="/assets/maintenanceSchedules" />
                    </>
                  )}
                  <MenuListItem title={<FormattedMessage id="menu.main.assets" defaultMessage="Assets List" />} icon={<ApartmentIcon />} route="/assets/assetsmanagement" />
                </List>
              </MenuListItemContainer>
            </SecuredModule>
            <SecuredModule permissions={getModule(Module.CompanySettings)}>
              {/* <MenuListItemContainer title={<FormattedMessage id="menu.main.settings.header" defaultMessage="Settings" />} icon={<SettingsIcon />} isOpen={openMenu === 'settings'} onToggle={() => toggleMenu('settings')}>
                <List className={classes.paddingY0}> */}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.settings.company" defaultMessage="Company" />} icon={<BusinessIcon />} route="/settings/company" /> */}
                  {/*<MenuListItem title={<FormattedMessage id="menu.main.settings.counter" defaultMessage="Counters" />} icon={<CounterIcon />} route="/settings/counters" />*/}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.setting.Events" defaultMessage="Events" />} route="/setting/events" /> */}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.setting.AppLog" defaultMessage="App Logs" />} icon={<ListAlt />} route="/setting/appLogs" /> */}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.setting.globalSettings" defaultMessage="Global Settings" />} icon={<Tune />} route="/setting/globalsettings" /> */}
                {/* </List>
              </MenuListItemContainer> */}
              <SecuredModule permissions={getModule(Module.OrganizationChart) || getModule(Module.CompanySettings)}>
                {/* <MenuListItemContainer
                  title={<FormattedMessage id="menu.main.userManagement" defaultMessage="User Management" />}
                  icon={<PeopleIcon className={classes.w110em} />} isOpen={openMenu === 'userManagement'} onToggle={() => toggleMenu('userManagement')}
                >
                  <List className={classes.paddingY0}>
                    <MenuListItem title={<FormattedMessage id="menu.main.settings.users" defaultMessage="Users" />} icon={<PeopleIcon className={classes.w110em} />} route="/UsersList" />
                    <MenuListItem title={<FormattedMessage id="menu.main.organization" defaultMessage="Organization" />} icon={<AccountTree />} route="/organization" />
                    <MenuListItem title={<FormattedMessage id="menu.main.settings.security" defaultMessage="Access Control" />} icon={<Security />} route="/securityModules" />
                  </List>
                </MenuListItemContainer> */}
              </SecuredModule>
            </SecuredModule>
            <SecuredModule permissions={getModule(Module.DirectMessages)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.communication" defaultMessage="Communication" />} icon={<MessageIcon className={classes.w110em} />} isOpen={openMenu === 'communication'} onToggle={() => toggleMenu('communication')}>
                <List className={classes.paddingY0}>
                  <MenuListItem title={<FormattedMessage id="menu.main.setting.directMessage" defaultMessage="Direct Message" />} icon={<MessageIcon className={classes.w110em} />} route="/communication/directMessage" />
                  <MenuListItem title={<FormattedMessage id="menu.main.settings.CompanyMessages" defaultMessage="Company Messages" />} icon={<Forum />} route="/communication/companyMessages" />
                  {/* Email Templates tab hidden for all users (admins and normal users); route still exists. */}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.settings.EmailTemplate" defaultMessage="Email Templates" />} icon={<MailOutlineIcon />} route="/communication/emailTemplates" /> */}
                </List>
              </MenuListItemContainer>
            </SecuredModule>
             <SecuredModule permissions={getModule(Module.LocationTracking)}>
              <MenuListItemContainer title={<FormattedMessage id="menu.main.location.tracking" defaultMessage="Map Management" />} icon={<LocationTrackingIcon className={classes.w110em} />} isOpen={openMenu === 'mapManagement'} onToggle={() => toggleMenu('mapManagement')}>
                <List className={classes.paddingY0}>
                  <MenuListItem title={<FormattedMessage id="menu.main.location.tracking.map" defaultMessage="Location Tracking" />} icon={<LocationTrackingIcon className={classes.w110em} />} route="/locationTracking" />
                  {/* Tracker hidden for all users (admins and normal users) */}
                  {/* <MenuListItem title={<FormattedMessage id="menu.main.location.tracker" defaultMessage="Tracker" />} icon={<Tracker className={classes.w110em} />} route="/locationTracking/tracker" /> */}
                </List>
              </MenuListItemContainer>
             </SecuredModule>
          </List>
        </SideNavigation>
      )}
      <div className={classes.mainColumn}>
        {oidcUser && !isFullscreenPage && !isShellFreePage && <TopHeaderDynamic />}
        <main className={classes.content}>
          {children}
        </main>
      </div>
    </div>
  )
}

export default Navigation
